import { useState, useRef, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import NewRegistrationPopup from "@/components/time-registration/NewRegistrationPopup";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useMonthTimeOverview } from "@/hooks/use-month-time-overview";
import { useTimeGaps } from "@/hooks/use-time-gaps";
import { useBillingRates } from "@/hooks/use-billing-rates";
import { useCloseMonth } from "@/hooks/use-close-month";
import { useMyTasks } from "@/hooks/use-my-tasks";
import CloseMonthStats from "@/components/close-month/CloseMonthStats";
import CloseMonthDayList from "@/components/close-month/CloseMonthDayList";
import CloseMonthAbsence from "@/components/close-month/CloseMonthAbsence";
import CloseMonthGapSummary from "@/components/close-month/CloseMonthGapSummary";
import CloseMonthGapOverview from "@/components/close-month/CloseMonthGapOverview";
import { dayRowId } from "@/components/close-month/day-status";
import { SrIcon } from "@/components/ui/SrIcon";
import { useTimer } from "@/contexts/TimerContext";

const MONTH_NAMES = [
  "",
  "januar",
  "februar",
  "mars",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "desember",
];

export default function CloseMonthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeUser, isImpersonating } = useImpersonation();
  const { activeTimer } = useTimer();
  const userId = activeUser?.id ?? null;

  const now = new Date();
  const paramYear = searchParams.get("year");
  const paramMonth = searchParams.get("month");
  const year = paramYear ? parseInt(paramYear) : now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const month = paramMonth ? parseInt(paramMonth) : now.getMonth() === 0 ? 12 : now.getMonth();

  const [isConfirming, setIsConfirming] = useState(false);
  const [absenceConfirmed, setAbsenceConfirmed] = useState(false);
  const [showNewPopup, setShowNewPopup] = useState(false);
  const [popupDate, setPopupDate] = useState<string | undefined>();
  const newBtnRef = useRef<HTMLButtonElement>(null);
  const inlineBtnRef = useRef<HTMLButtonElement>(null);

  const monthName =
    (MONTH_NAMES[month]?.charAt(0).toUpperCase() ?? "") +
    (MONTH_NAMES[month]?.slice(1) ?? "");

  // Data hooks
  const { data: dayOverview = [] } = useMonthTimeOverview(userId, year, month);
  const targetDate = new Date(year, month - 1, 15);
  const { data: gapData } = useTimeGaps(userId, targetDate);
  const { data: billingData } = useBillingRates(year);
  const { mutate: closeMonth, isPending, isSuccess, error } = useCloseMonth();
  const { data: myTasksData } = useMyTasks(userId);

  const monthData = billingData?.employees
    .find((e) => e.userId === userId)
    ?.months.find((m) => m.month === month);

  const loggedHours = monthData?.loggedHours ?? gapData?.totalRegisteredHours ?? 0;
  const capacity = monthData?.capacity ?? gapData?.totalExpectedHours ?? 0;
  const absenceHours = monthData?.absenceHours ?? 0;

  // Check for unapproved tasks that have logged hours
  const pendingTasks = useMemo(() => {
    const tasks = myTasksData?.tasks ?? [];
    return tasks.filter(
      (t) => t.task_status !== "approved" && t.logged_hours > 0
    );
  }, [myTasksData]);

  const hasPendingTasks = pendingTasks.length > 0;

  const handleClose = () => {
    closeMonth({ year, month });
  };

  const scrollToDay = (date: string) => {
    document.getElementById(dayRowId(date))?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleAddEntry = (date: string) => {
    setPopupDate(date);
    setShowNewPopup(true);
  };

  // Keyboard shortcut: N opens "Ny registrering" (unless typing in input)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (showNewPopup) return;
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return;
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        setPopupDate(undefined);
        setShowNewPopup(true);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [showNewPopup]);

  return (
    <div className="page-container animate-fade-in max-w-4xl">
      {/* Back link */}
      <button
        onClick={() => navigate("/mine-oppgaver")}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
      >
        <SrIcon name="arrow-left" className="w-4 h-4"  />
        Tilbake til Mine oppgaver
      </button>

      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] text-foreground tracking-wide">
            Lukk måned – {monthName} {year}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Kontroller timer og fravær før du lukker måneden
          </p>
        </div>
        <Button
          ref={newBtnRef}
          size="sm"
          onClick={() => { setPopupDate(undefined); setShowNewPopup(true); }}
          className="w-full xl:w-auto"
        >
          <SrIcon name="plus" className="w-4 h-4 mr-1"  />
          Ny registrering
        </Button>
      </div>

      {isSuccess ? (
        <div className="space-y-4">
          <Alert className="border-green-200 bg-green-50">
            <SrIcon name="check-circle2" className="h-4 w-4 text-green-600"  />
            <AlertDescription className="text-green-900">
              {monthName} {year} er nå lukket og låst.
            </AlertDescription>
          </Alert>
          <Button onClick={() => navigate("/mine-oppgaver")}>
            Tilbake til Mine oppgaver
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* VIB-3034: hullene øverst, med kalender som ruller til dagen */}
          <CloseMonthGapOverview
            gaps={gapData?.gaps ?? []}
            monthLabel={monthName.toLowerCase()}
            year={year}
            onJump={scrollToDay}
          />

          {/* Stats */}
          <CloseMonthStats
            loggedHours={loggedHours}
            capacity={capacity}
            absenceHours={absenceHours}
            billingRate={monthData?.rate ?? null}
          />

          {/* Absence management */}
          <CloseMonthAbsence userId={userId} year={year} month={month} />

          {/* Pending tasks warning */}
          {hasPendingTasks && (
            <Alert className="border-amber-200 bg-amber-50">
              <SrIcon name="alert-circle" className="h-4 w-4 text-amber-600"  />
              <AlertDescription className="text-amber-900 text-sm">
                <p className="mb-2">
                  Du har {pendingTasks.length} oppgave{pendingTasks.length !== 1 ? "r" : ""} som venter på godkjenning.
                  Du kan likevel lukke måneden. Timene teller først i måneden oppgaven godkjennes.
                </p>
                <ul className="space-y-1">
                  {pendingTasks.map((t) => (
                    <li key={t.id} className="flex items-center gap-2 text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                      <span className="font-medium">{t.project_name}</span>
                      <span className="text-amber-700">—</span>
                      <span>{t.task_description}</span>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Day list: alle tidligere ukedager, gruppert per uke */}
          <div>
            <span className="text-xs text-muted-foreground">
              Klikk en dag for å se detaljer
            </span>
          </div>
          <CloseMonthDayList
            days={dayOverview}
            gaps={gapData?.gaps ?? []}
            onAddEntry={handleAddEntry}
          />

          {activeTimer && (
            <Alert className="border-red-200 bg-red-50">
              <SrIcon name="alert-circle" className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-900 text-sm">
                Stopp den aktive timeren på «{activeTimer.projectName}» før du lukker måneden.
              </AlertDescription>
            </Alert>
          )}

          {isImpersonating && (
            <Alert className="border-amber-200 bg-amber-50">
              <SrIcon name="alert-circle" className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-amber-950 text-sm">
                Bare den ansatte selv kan lukke måneden. Avslutt visning som annen bruker først.
              </AlertDescription>
            </Alert>
          )}

          {/* Footer: confirmation flow */}
          <div className="pt-4 border-t border-border/50 space-y-4">
            {/*
              VIB-2083: si hullene høyt før avslutning. Daglista farger hver
              dag, men med 30 rader er det lett å avslutte uten å ha sett dem.
              Varsler kun — knappen er ikke sperret (Erik 28.09).
            */}
            <CloseMonthGapSummary
              gaps={gapData?.gaps ?? []}
              monthLabel={monthName.toLowerCase()}
              onJump={scrollToDay}
            />
            {!isConfirming ? (
              <div className="flex items-center xl:justify-end">
                <Button
                  size="lg"
                  onClick={() => setIsConfirming(true)}
                  disabled={isPending || !!activeTimer || isImpersonating}
                  className="w-full xl:w-auto"
                >
                  Lukk måned
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Absence confirmation checkbox */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={absenceConfirmed}
                    onCheckedChange={(v) => setAbsenceConfirmed(v === true)}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-foreground">
                    Jeg bekrefter at alle timer og fravær (ferie, sykdom, permisjon) for {monthName.toLowerCase()} {year} er ført.
                  </span>
                </label>

                <Alert className="border-red-200 bg-red-50">
                  <SrIcon name="alert-circle" className="h-4 w-4 text-red-600"  />
                  <AlertDescription className="text-red-900 text-sm">
                    Når du lukker måneden, låses timer og fravær for {monthName.toLowerCase()}. En administrator kan bare gjenåpne måneden med begrunnelse.
                  </AlertDescription>
                </Alert>

                {error && (
                  <Alert className="border-red-200 bg-red-50">
                    <SrIcon name="alert-circle" className="h-4 w-4 text-red-600"  />
                    <AlertDescription className="text-red-900 text-sm">
                      {(error as Error).message}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex flex-col-reverse xl:flex-row gap-2 xl:justify-end">
                  <Button
                    variant="outline"
                    onClick={() => { setIsConfirming(false); setAbsenceConfirmed(false); }}
                    disabled={isPending}
                    className="h-12 xl:h-10 w-full xl:w-auto"
                  >
                    Gå tilbake
                  </Button>
                  <Button
                    onClick={handleClose}
                    disabled={isPending || !absenceConfirmed || !!activeTimer || isImpersonating}
                    className="h-12 xl:h-10 w-full xl:w-auto"
                  >
                    {isPending ? "Lukker..." : "Bekreft lukking"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hidden anchor for inline "Ny registrering" buttons */}
      <button ref={inlineBtnRef} className="hidden" aria-hidden />

      {showNewPopup && (
        <NewRegistrationPopup
          onClose={() => setShowNewPopup(false)}
          anchorRef={popupDate ? inlineBtnRef : newBtnRef}
          initialDate={popupDate}
        />
      )}
    </div>
  );
}
